// "The promise": the year's last ten seconds, hold to make a wish → one second, and fireworks burst as blossoms → 365 new days bloom on white stems → her painting emerges.
const BLOOMS = [
  [-0.55, 2.7, 15.0], [0.35, 3.1, 15.4], [-0.15, 2.2, 15.9], [0.75, 2.4, 16.3], [-0.95, 3.2, 16.7], [0.1, 3.6, 17.1], [1.2, 2.9, 17.4], [-1.35, 2.5, 17.7],
].map(([th, y, t], i) => ({ th, y, r: 0.55 + (i % 3) * 0.12, t, color: ["#C9DDF7", "#FFFFFF", "#A9C8F0"][i % 3] }));
export const promise = {
  slug: "the-promise",
  duration: 22,
  music: "gymnopedie-1",
  musicAt: 10,
  captionTail: "In one second, everything can change.",
  stages: [
    { id: "lastyear", at: 0, hint: "the last moments of the year" },
    { id: "wish", at: 8.5, hint: "hold to make a wish" },
    { id: "midnight", at: 10, hint: "one second" },
    { id: "days", at: 14.8, hint: "tap to bloom more" },
    { id: "settle", at: 18, hint: "" },
  ],
  hold: { at: 9.6, need: 2, auto: 8 },
  // the last night of the year over the city; at midnight, fireworks burst as blossoms on white stems
  layers: [
    { src: "/arcs/promise/eve.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, opacity: [[0, 1], [10, 1], [11, 0.92]], show: [-1, 0, 15, 17.5] },
    { src: "/arcs/promise/fireworks.webp", add: true, sway: 0.05, shimmer: 0.3, glow: 0.3, th: 0, y: 2.7, w: 6.4, h: 4.3, r: 4.4, show: [10, 11.2, 15.5, 17.5] },
    // the painting's own motif: pale blue and white blossoms opening on fine white stems out of the deep navy
    { src: "/arcs/promise/stems.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, sway: 0, glow: 0.2, shimmer: 0.15, show: [14.5, 17, 99, 99] },
  ],
  touch: { kind: "bloom", from: 8.5, until: 18, colors: ["#C9DDF7", "#FFFFFF", "#A9C8F0", "#E3EEFC"] },
  blooms: BLOOMS,
  moments: [...Array.from({ length: 10 }, (_, i) => ({ text: String(10 - i), from: i, to: i + 1 })), { text: "365 new days", from: 15.5, to: 21 }],
  line: { text: "Every new year arrives with a promise: hope itself.", from: 16.5, to: 29, pos: "low" },
  cues: [[10.02, "bell"], [10.6, "pop"], [11.3, "bell"], [12.1, "pop"], [12.8, "pop"], [13.6, "pop"], [14.1, "bellSoft"]],
  ambient: { scene: "winter", until: 99, then: [[10, "reveal"]] },
  look: { start: 0.12, lift: [0, 1], end: 0.12, yaw: [[0, 0], [22, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "promise", pos: "low" },
  speed: [[0, 1], [22, 1]],
  reveal: { style: "flow", from: "bottom", duration: 10 },
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform vec4 uBloom[20];
uniform vec3 uBloomCol[20];
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface, uStill;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
// a firework burst as a blossom: petal strokes flung out, drooping and fading
float burst(vec2 q, float a, float seed){
  if (a < 0.0 || a > 3.5) return 0.0;
  q.y += 0.12 * a * a;
  float R = 1.1 * (1.0 - exp(-a * 2.2));
  float ang = atan(q.y, q.x) + seed;
  float sect = 6.2832 / 14.0;
  float k = floor(ang / sect);
  float da = (ang - (k + 0.5) * sect) * length(q);
  float along = length(q) - R * 0.72;
  float petal = 1.0 - smoothstep(0.8, 1.0, length(vec2(along / (0.3 * R + 0.02), da / (0.045 + 0.04 * R))));
  return petal * step(0.25, hash1(k + seed * 7.0)) * (1.0 - smoothstep(2.0, 3.5, a));
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float tex = snoise(vec3(vPos.x * 1.5 + vPos.y * 0.6, vPos.y * 2.5, vPos.z * 1.5)) * 0.5 + 0.5;
  vec3 col = mix(vec3(0.04, 0.07, 0.2), vec3(0.1, 0.15, 0.34), tex * 0.8 + n * 0.2);
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    vec2 P = vec2(X, vPos.y);
    // a wish: a soft warm light gathering while you hold
    col += vec3(1.0, 0.92, 0.75) * exp(-length((P - vec2(0.0, 2.4)) * vec2(0.7, 1.0)) * 1.3) * (0.1 + 0.4 * uStill) * (1.0 - smoothstep(10.0, 12.0, uT));
    // midnight: fireworks in pale blue and white blossom
    for (int i = 0; i < 9; i++){
      float fi = float(i);
      vec2 c = vec2((hash1(fi + 1.0) - 0.5) * 10.0, 3.0 + 1.5 * hash1(fi + 4.0));
      float b = burst((P - c) / (0.8 + 0.5 * hash1(fi + 2.0)), uT - (10.0 + fi * 0.5), fi * 1.7) * 0.9;
      col = mix(col, mix(vec3(0.75, 0.86, 1.0), vec3(1.0), step(0.5, hash1(fi + 9.0))), b);
    }
    // 365 new days: blossoms on fine white stems rising out of the navy
    for (int i = 0; i < 20; i++){
      vec4 bl = uBloom[i];
      if (bl.w <= 0.0) continue;
      float bx = bl.x * uRadius;
      float grow = smoothstep(bl.w, bl.w + 1.4, uT);
      float top = bl.y * grow;
      if (abs(P.x - bx) > bl.z + 0.35 || P.y > top + bl.z * 0.6) continue;
      float stem = (1.0 - smoothstep(0.006, 0.014, abs(P.x - bx - 0.04 * sin(P.y * 2.0 + bl.x * 9.0) * P.y / max(bl.y, 0.1)))) * step(P.y, top);
      col = mix(col, vec3(0.92, 0.94, 0.98), stem * 0.85);
      float open = smoothstep(bl.w + 1.0, bl.w + 2.2, uT);
      for (int j = 0; j < 5; j++){
        float fj = float(j);
        vec2 fc = vec2(bx + 0.04 * sin(top * 2.0 + bl.x * 9.0), top) + vec2(cos(fj * 1.26 + bl.x * 5.0), sin(fj * 1.26 + bl.x * 5.0) * 0.7) * bl.z * 0.32 * open;
        vec2 q = P - fc;
        float pa = atan(q.y, q.x) + fj + uT * 0.05;
        float fl = 1.0 - smoothstep(0.9, 1.0, length(q) / (bl.z * 0.2 * open * (0.55 + 0.45 * pow(abs(cos(pa * 2.5)), 0.6)) + 0.0001));
        col = mix(col, uBloomCol[i] * (0.85 + 0.2 * cos(pa * 5.0)), fl * step(0.01, open));
      }
    }
    local = vec2(X, P.y - uCy);
  } else {
    local = vPos.xz;
  }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
